import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { UserMutationError } from "../errors/user-mutation.errors";
import { usersService } from "../services";

export const useCreateUserMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: {
			name: string;
			email: string;
			cpf: string;
			role: string;
			groupIds?: number[];
		}) => usersService.createUser(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["users"] });
		},

		onError: (error: unknown) => {
			if (error instanceof AxiosError) {
				const status = error.response?.status;
				const errorType = error.response?.data?.error?.type;

				if (status === 409 && errorType === "UserAlreadyExistsException") {
					throw new Error(UserMutationError.USER_ALREADY_EXISTS);
				}
			}

			throw new Error(UserMutationError.UNEXPECTED_ERROR);
		},
	});
};

export const useUpdateUserMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			userId,
			data,
		}: {
			userId: string;
			data: { name: string; email: string; groupIds?: number[] };
		}) => usersService.updateUser(userId, data),

		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ["users"] });
			queryClient.invalidateQueries({
				queryKey: ["user", variables.userId],
			});
		},

		onError: () => {
			throw new Error(UserMutationError.UNEXPECTED_ERROR);
		},
	});
};

export const useUpdateUserStatusMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			userId,
			data,
		}: {
			userId: string;
			data: { isActive: boolean };
		}) => usersService.updateUserStatus(userId, data),

		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["users"] });
		},

		onError: () => {
			throw new Error(UserMutationError.UNEXPECTED_ERROR);
		},
	});
};
